import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import AuthModal from '@/components/feature/AuthModal';

export default function Navbar() {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [authOpen, setAuthOpen] = useState(false);
  const [authTab, setAuthTab] = useState<'login' | 'register'>('login');
  const location = useLocation();

  const isHomePage = location.pathname === '/';

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location.pathname]);

  // Home page has its own nav inside HeroSection
  if (isHomePage) return null;

  const navLinks = [
    { path: '/', label: 'Home' },
    { path: '/products', label: 'Our Brands' },
    { path: '/about', label: 'About Us' },
    { path: '/investors', label: 'Investors' },
    { path: '/contact', label: 'Contact' },
    { path: '/faq', label: 'FAQ' },
  ];

  const isActive = (path: string) => location.pathname === path;

  const openAuth = (tab: 'login' | 'register' = 'login') => {
    setAuthTab(tab);
    setAuthOpen(true);
  };

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-white border-b border-stone-200">
      {/* Main nav */}
      <div className="w-full px-4 sm:px-6 lg:px-16 xl:px-24">
        <div className="flex flex-col items-center pt-5 pb-3">

          {/* Logo — full logo image with text baked in */}
          <Link to="/" className="flex flex-col items-center mb-3">
            <div
              className="w-28 h-28 md:w-32 md:h-32 rounded-full overflow-hidden flex items-center justify-center"
              style={{ border: '2px solid #e8d5b7', background: '#fff' }}
            >
              <img
                src="https://static.readdy.ai/image/3301efb4abc894ce1097fc48d2d46708/299f592c2e3e9cf1636e28f1b3d51d14.png"
                alt="Faasley Foods"
                className="w-full h-full object-contain"
              />
            </div>
          </Link>

          {/* Nav row */}
          <div className="hidden md:flex items-center justify-center w-full relative">
            {/* Nav links centered */}
            <div className="flex items-center gap-7">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`text-sm font-medium transition-colors whitespace-nowrap pb-0.5 ${
                    isActive(link.path)
                      ? 'text-amber-600 border-b border-amber-500'
                      : 'text-stone-600 hover:text-stone-900'
                  }`}
                >
                  {link.label}
                </Link>
              ))}
            </div>

            {/* Right icons — absolute */}
            <div className="absolute right-0 flex items-center gap-1">
              <button className="w-9 h-9 flex items-center justify-center text-stone-500 hover:text-stone-900 transition-colors cursor-pointer rounded-full hover:bg-stone-50">
                <i className="ri-search-line text-base"></i>
              </button>
              <button
                onClick={() => openAuth('login')}
                className="w-9 h-9 flex items-center justify-center text-stone-500 hover:text-stone-900 transition-colors cursor-pointer rounded-full hover:bg-stone-50"
              >
                <i className="ri-user-line text-base"></i>
              </button>
              <Link
                to="/products"
                className="w-9 h-9 flex items-center justify-center text-stone-500 hover:text-stone-900 transition-colors rounded-full hover:bg-stone-50"
              >
                <i className="ri-shopping-bag-line text-base"></i>
              </Link>
            </div>
          </div>

          {/* Mobile toggle */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="md:hidden flex items-center gap-2 text-stone-700 text-sm cursor-pointer mt-1"
          >
            <i className={`ri-${isMobileMenuOpen ? 'close' : 'menu-3'}-line text-xl`}></i>
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {isMobileMenuOpen && (
        <div className="md:hidden bg-white border-t border-stone-100 px-4 py-4 space-y-1">
          {navLinks.map((link) => (
            <Link
              key={link.path}
              to={link.path}
              onClick={() => setIsMobileMenuOpen(false)}
              className={`block py-2.5 px-4 rounded-xl text-sm font-medium transition-colors ${
                isActive(link.path) ? 'text-amber-600 bg-amber-50' : 'text-stone-700 hover:bg-stone-50'
              }`}
            >
              {link.label}
            </Link>
          ))}
          <div className="pt-2 flex gap-2">
            <button
              onClick={() => { setIsMobileMenuOpen(false); openAuth('login'); }}
              className="flex-1 py-2.5 px-4 rounded-xl text-sm font-medium text-stone-700 border border-stone-200 hover:bg-stone-50 transition-colors cursor-pointer whitespace-nowrap"
            >
              Sign In
            </button>
            <Link
              to="/products"
              onClick={() => setIsMobileMenuOpen(false)}
              className="flex-1 py-2.5 px-4 rounded-xl text-sm font-semibold text-white bg-amber-500 hover:bg-amber-600 transition-colors text-center whitespace-nowrap"
            >
              Shop Now
            </Link>
          </div>
        </div>
      )}

      <AuthModal
        isOpen={authOpen}
        onClose={() => setAuthOpen(false)}
        defaultTab={authTab}
      />
    </nav>
  );
}
